import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import MangaApiService from '../../services/MangaApiService';
import MangaAccessNotice from './MangaAccessNotice';
import MangaStudioNav from './MangaStudioNav';
import ExternalLinkService from '../../services/ExternalLinkService';
import { fetchAllTeamMangas,
    getMangaId,
    getMangaTitle,
    getRejectionReason,
    getTeamId,
    isRejectedStatus,
    resolveDonationLinks,
    splitLinks,
    translateStatus,
    unwrapResponse,
    getMangaErrorMessage,
} from './mangaStudioUtils';
import teamIcon from '../../assets/ic_profile.svg';

const RECENT_LIMIT = 8;

const MangaStudioPage = () => {
    const [team, setTeam] = useState(null);
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);

    const [mangaList, setMangaList] = useState([]);
    const [mangaLoading, setMangaLoading] = useState(false);
    const [mangaError, setMangaError] = useState(null);

    const isAllowed = MangaApiService.isAllowedDomain();
    const hasSession = !!localStorage.getItem('sessionKey');
    const teamId = getTeamId(team);

    useEffect(() => {
        document.title = 'animix - Студия манги';
    }, []);

    const loadTeam = async () => {
        if (!hasSession || !isAllowed) {
            return;
        }
        setTeamLoading(true);
        setTeamError(null);
        try {
            const response = await MangaApiService.getMyTeam();
            const payload = unwrapResponse(response);
            setTeam(payload || null);
        } catch (err) {
            if (err?.response?.status === 404) {
                setTeam(null);
            } else {
                setTeamError(getMangaErrorMessage(err, 'Не удалось загрузить команду.'));
            }
        } finally {
            setTeamLoading(false);
        }
    };

    const loadTeamMangas = async (id) => {
        if (!id) {
            setMangaList([]);
            return;
        }
        setMangaLoading(true);
        setMangaError(null);
        try {
            setMangaList(await fetchAllTeamMangas(id));
        } catch (err) {
            setMangaError(getMangaErrorMessage(err, 'Не удалось загрузить релизы.'));
        } finally {
            setMangaLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed || !hasSession) {
            return;
        }
        loadTeam();
    }, [isAllowed, hasSession]);

    useEffect(() => {
        if (!teamId) {
            setMangaList([]);
            return;
        }
        loadTeamMangas(teamId);
    }, [teamId]);

    useEffect(() => {
        let isActive = true;
        const resolveLogo = async () => {
            if (!team) {
                setLogoUrl(null);
                return;
            }
            const fileId = team.logoFileId || team.logo_file_id || team.logo?.fileId;
            const bucket = team.logoStorageBucket || team.logo_storage_bucket || team.logo?.bucket;
            if (!fileId || !bucket) {
                setLogoUrl(null);
                return;
            }
            try {
                const resolved = await MangaApiService.resolveFileUrlAsync({ fileId, bucket }, bucket);
                if (isActive) {
                    setLogoUrl(resolved || null);
                }
            } catch (error) {
                if (isActive) {
                    setLogoUrl(null);
                }
            }
        };
        resolveLogo();
        return () => {
            isActive = false;
        };
    }, [team]);

    const donationLinks = useMemo(() => {
        const raw = resolveDonationLinks(team);
        if (Array.isArray(raw)) {
            return raw;
        }
        return splitLinks(raw);
    }, [team]);

    const teamStatus = team?.status || team?.state || team?.moderationStatus;
    const teamStatusLabel = translateStatus(teamStatus);
    const teamRejectionReason = getRejectionReason(team);

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    // Titles that need the team's attention first, then the newest ones.
    const recentMangas = [...mangaList]
        .sort((left, right) => {
            const weight = (manga) => (isRejectedStatus(manga?.status) ? 0 : String(manga?.status).toUpperCase() === 'PENDING' ? 1 : 2);
            return weight(left) - weight(right) || Number(getMangaId(right) || 0) - Number(getMangaId(left) || 0);
        })
        .slice(0, RECENT_LIMIT);

    return (
        <PageShell className="manga-studio-page">
            <PageHeader title="Студия манги" />
            <MangaStudioNav showBanner />

            {!hasSession ? (
                <div className="manga-inline-warning">Войдите, чтобы управлять командой и релизами.</div>
            ) : null}
            {teamError ? <div className="manga-inline-warning">{teamError}</div> : null}

            {!team && !teamLoading ? (
                <div className="manga-panel studio-empty">
                    <div className="manga-studio-summary-icon"><img src={teamIcon} alt="" aria-hidden="true" /></div>
                    <h2>Команда ещё не создана</h2>
                    <p className="manga-help">Команда нужна, чтобы публиковать мангу и главы. Все новые релизы проходят модерацию.</p>
                    <Link className="ui-btn" to="/manga/studio/team">Создать команду</Link>
                </div>
            ) : (
                <div className="studio-layout">
                    <aside className="studio-layout__side">
                        <div className="manga-panel">
                            {teamLoading ? <div className="loader" /> : null}
                            {team ? (
                                <>
                                    <div className="manga-studio-team">
                                        <div className="manga-studio-team-logo">
                                            {logoUrl ? (
                                                <img src={logoUrl} alt={team.name || team.title || 'Логотип команды'} />
                                            ) : (
                                                <div className="manga-studio-team-placeholder">
                                                    <img src={teamIcon} alt="" aria-hidden="true" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="manga-studio-team-info">
                                            <div className="manga-studio-team-name">{team.name || team.title || 'Команда'}</div>
                                            {teamStatus ? <span className="studio-status">{teamStatusLabel}</span> : null}
                                        </div>
                                    </div>
                                    <div className="manga-help">
                                        {team.description || 'Добавьте описание, чтобы читатели узнали о вас.'}
                                    </div>
                                    {isRejectedStatus(teamStatus) && teamRejectionReason ? (
                                        <div className="manga-inline-warning">Причина отклонения: {teamRejectionReason}</div>
                                    ) : null}
                                    {donationLinks.length > 0 ? (
                                        <div className="manga-studio-links">
                                            <div className="manga-studio-links-title">Донаты</div>
                                            {donationLinks.map((link, index) => (
                                                <a
                                                    key={`${link}-${index}`}
                                                    href={link}
                                                    rel="noreferrer"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        ExternalLinkService.openExternal(link);
                                                    }}
                                                >
                                                    {link}
                                                </a>
                                            ))}
                                        </div>
                                    ) : null}
                                    <Link className="button-sec" to="/manga/studio/team">Управлять командой</Link>
                                </>
                            ) : null}
                        </div>
                    </aside>

                    <div className="manga-panel">
                        <div className="studio-card__head">
                            <h2>Релизы</h2>
                            <div className="studio-card__actions">
                                <button className="button-sec" onClick={() => loadTeamMangas(teamId)} disabled={!teamId}>Обновить</button>
                                <Link className="ui-btn ui-btn--sm" to="/manga/studio/manga">Новая манга</Link>
                            </div>
                        </div>
                        {mangaLoading ? <div className="loader" /> : null}
                        {mangaError ? <div className="manga-inline-warning">{mangaError}</div> : null}
                        <div className="manga-studio-list">
                            {teamId && mangaList.length > 0 ? (
                                recentMangas.map((manga) => {
                                    const id = getMangaId(manga);
                                    const status = manga.status || manga.state || manga.moderationStatus;
                                    const rejectionReason = getRejectionReason(manga);
                                    return (
                                        <div key={id || getMangaTitle(manga)} className="manga-studio-release-item">
                                            <div>
                                                <div className="manga-studio-item-title">{getMangaTitle(manga)}</div>
                                                {status ? <div className="manga-studio-item-meta">{translateStatus(status)}</div> : null}
                                                {isRejectedStatus(status) && rejectionReason ? (
                                                    <div className="manga-studio-item-meta manga-status-reason-text">Причина: {rejectionReason}</div>
                                                ) : null}
                                            </div>
                                            <Link to="/manga/studio/manga" className="manga-studio-link">Управлять</Link>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="manga-empty">Пока нет релизов. Создайте первый тайтл.</div>
                            )}
                        </div>
                        {mangaList.length > RECENT_LIMIT ? (
                            <Link className="button-sec" to="/manga/studio/manga">Все тайтлы ({mangaList.length})</Link>
                        ) : null}
                    </div>
                </div>
            )}
        </PageShell>
    );
};

export default MangaStudioPage;
